import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { CornerDownLeft, Loader2, Search } from "lucide-react";

import {
  buildQuickSwitcherCatalogPages,
  quickNavPageVisibleToKeys,
  QUICK_SWITCHER_MODULE_ROOT_PATHS,
} from "@/lib/quickSwitcherCatalog";
import {
  compareQuickNavPages,
  filterQuickNavPagesForQuery,
} from "@/lib/quickSwitcherPageRank";
import {
  buildMrpPurchaseInvoiceDetailPath,
  buildSalesOrderDetailPath,
  buildShippingGosiDetailsPath,
  buildShippingShipmentDetailsPath,
} from "@/lib/hubEntityDetailPaths";
import { salesOrderStatusLabel } from "@/lib/salesOrderHubStatus";
import { buildManufacturingBatchDetailsPath } from "@/lib/manufacturingEditBatchPath";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import {
  collectPageFieldTargets,
  filterPageFieldTargets,
  jumpToPageField,
  type PageFieldTarget,
} from "@/lib/pageFieldSearch";
import { parseQuickSwitcherScopedQuery } from "@/lib/quickSwitcherScopedQuery";
import { inventoryAdjustScanTarget } from "@/pages/shipping/inventoryAdjustScanModel";
import { inventoryBoxShortCodeFromHex } from "@integrator-src/api/inventory-box/inventory-box-scan";
import { apiFetch, ApiError } from "@/lib/api";
import { buildMrpSkuDetailPath, type MrpPurchaseOrder, type MrpSkuMasterRow } from "@/lib/mrp";
import { useIntegratorMe } from "@/lib/me-query";
import { appsVisibleToUser, hubModuleAllowsWrite } from "@/lib/subapp-access";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { OpenInventoryDiscrepancySkuBadge } from "@/components/manufacturing/OpenInventoryDiscrepancySkuBadge";
import { cn } from "@/lib/utils";
import { registerViewportLayoutListeners } from "@/lib/useViewportLayoutEvents";
import { useOpenInventoryDiscrepancySkus } from "@/lib/useOpenInventoryDiscrepancySkus";
import { searchWiki, type WikiSearchResult } from "@/lib/wiki";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";

type QuickNavPage = {
  kind: "page";
  path: string;
  label: string;
  group: string;
  /** Extra tokens for quick search (abbreviations, alternate spellings). */
  searchText?: string;
};

type QuickSkuRow = { kind: "sku"; sku: string; subtitle: string | null };
type QuickWikiRow = { kind: "wiki"; id: string; title: string; subtitle: string | null };
type QuickBatchRow = { kind: "batch"; batchId: number; label: string; subtitle: string | null };
type QuickPoRow = {
  kind: "po";
  purchaseOrderId: number;
  label: string;
  subtitle: string | null;
};

type QuickSalesOrderRow = {
  kind: "so";
  salesOrderId: number;
  label: string;
  subtitle: string | null;
};

type QuickShipmentRow = {
  kind: "shipment";
  shipmentId: number;
  label: string;
  subtitle: string | null;
};

type QuickGipiRow = {
  kind: "gipi";
  purchaseInvoiceId: number;
  label: string;
  subtitle: string | null;
  listSearchPrefill: string | null;
};

type QuickGosiRow = {
  kind: "gosi";
  mvDocumentId: number;
  label: string;
  subtitle: string | null;
};

type QuickRfqRow = {
  kind: "rfq";
  rfqId: number;
  label: string;
  subtitle: string | null;
};

type QuickSupplierRow = {
  kind: "supplier";
  supplierId: number;
  label: string;
  subtitle: string | null;
};

type QuickRmaRow = {
  kind: "rma";
  rmaId: number;
  label: string;
  subtitle: string | null;
};

type QuickQualityRecordRow = {
  kind: "quality-record";
  recordKey: string;
  path: string;
  label: string;
  subtitle: string | null;
};

type QuickPurchaseInvoiceSearchRow = {
  id: string;
  mv_number: string | null;
  mv_status: string | null;
  supplier_name: string | null;
  purchase_order_number: string | null;
};

type QuickFieldRow = {
  kind: "field";
  target: PageFieldTarget;
};

type QuickSwitcherRow =
  | QuickNavPage
  | QuickSkuRow
  | QuickWikiRow
  | QuickBatchRow
  | QuickPoRow
  | QuickSalesOrderRow
  | QuickShipmentRow
  | QuickGipiRow
  | QuickGosiRow
  | QuickRfqRow
  | QuickSupplierRow
  | QuickRmaRow
  | QuickQualityRecordRow
  | QuickFieldRow;

function quickSwitcherRowPath(row: QuickSwitcherRow): string {
  if (row.kind === "field") return "#";
  if (row.kind === "page") return row.path;
  if (row.kind === "sku") return buildMrpSkuDetailPath(row.sku);
  if (row.kind === "batch") return buildManufacturingBatchDetailsPath(row.batchId);
  if (row.kind === "po") return `/mrp/purchase-orders/${row.purchaseOrderId}`;
  if (row.kind === "gipi") {
    return buildMrpPurchaseInvoiceDetailPath(row.purchaseInvoiceId, row.listSearchPrefill);
  }
  if (row.kind === "gosi") return buildShippingGosiDetailsPath(row.mvDocumentId);
  if (row.kind === "rfq") return `/mrp/rfqs/${String(row.rfqId)}`;
  if (row.kind === "supplier") return `/mrp/suppliers?supplier=${String(row.supplierId)}`;
  if (row.kind === "rma") return `/quality/rmas/${String(row.rmaId)}`;
  if (row.kind === "quality-record") return row.path;
  if (row.kind === "so") return buildSalesOrderDetailPath(row.salesOrderId);
  if (row.kind === "shipment") {
    return buildShippingShipmentDetailsPath(row.shipmentId);
  }
  return `/wiki/docs/${encodeURIComponent(row.id)}`;
}

function isPrimarySameTabLinkClick(event: ReactMouseEvent<HTMLAnchorElement>): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

function QuickSwitcherOption({
  flatIndex,
  active,
  to,
  onMouseEnter,
  onSameTabNavigate,
  children,
}: {
  flatIndex: number;
  active: boolean;
  to: string;
  onMouseEnter: () => void;
  onSameTabNavigate: () => void;
  children: ReactNode;
}) {
  return (
    <li role="presentation">
      <Link
        to={to}
        role="option"
        id={`quick-switch-${flatIndex}`}
        aria-selected={active}
        onMouseEnter={onMouseEnter}
        onClick={(event) => {
          if (!isPrimarySameTabLinkClick(event)) return;
          onSameTabNavigate();
        }}
        className={cn(
          "flex w-full flex-col rounded-md px-2 py-1.5 text-left text-sm no-underline transition-colors",
          active ? "bg-brand-50 text-brand-900" : "text-slate-700 hover:bg-slate-50",
        )}
      >
        {children}
      </Link>
    </li>
  );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function visibleKeysForMe(me: Parameters<typeof appsVisibleToUser>[0]): Set<string> {
  return new Set(appsVisibleToUser(me).map((application) => application.key));
}

type SkuListResponse = {
  skus: MrpSkuMasterRow[];
  page: number;
  page_size: number;
  total_count: number;
};

function isGoToShortcut(event: globalThis.KeyboardEvent): boolean {
  if (event.key !== "/" && event.code !== "Slash") return false;
  return event.ctrlKey || event.metaKey;
}

function batchQuickSearchLabel(batchId: number): string {
  return `Batch ${batchId}`;
}

function batchQuickSearchSubtitle(row: {
  location_label: string;
  mfr_ship_by_date: string | null;
  ship_to_recipient: string | null;
  manufacturer_sales_order_number: string | null;
  gso_numbers?: string[];
}): string | null {
  const gsoDisplay = (row.gso_numbers ?? []).join(", ");
  const pieces = [
    row.location_label,
    row.mfr_ship_by_date != null ? `ship by ${row.mfr_ship_by_date}` : null,
    gsoDisplay !== "" ? gsoDisplay : null,
    row.manufacturer_sales_order_number,
    row.ship_to_recipient,
  ].filter((piece): piece is string => piece != null && piece.trim() !== "");
  return pieces.length > 0 ? pieces.join(" · ") : null;
}

function salesOrderQuickSearchLabel(number: string | null, salesOrderId: number): string {
  const trimmed = number?.trim() ?? "";
  return trimmed !== "" ? trimmed : `Sales order ${String(salesOrderId)}`;
}

function salesOrderQuickSearchSubtitle(row: {
  client_name: string | null;
  status: string | null;
  sgs_ship_by_date: string | null;
  customer_purchase_order_number: string | null;
}): string | null {
  const pieces = [
    row.client_name,
    row.status != null ? salesOrderStatusLabel(row.status) : null,
    row.sgs_ship_by_date != null ? `ship by ${row.sgs_ship_by_date}` : null,
    row.customer_purchase_order_number != null && row.customer_purchase_order_number.trim() !== ""
      ? `PO ${row.customer_purchase_order_number.trim()}`
      : null,
  ].filter((piece): piece is string => piece != null && piece.trim() !== "");
  return pieces.length > 0 ? pieces.join(" · ") : null;
}

function purchaseInvoiceQuickSearchLabel(row: {
  id: string;
  mv_number: string | null;
}): string {
  const trimmed = row.mv_number?.trim() ?? "";
  return trimmed !== "" ? trimmed : `GIPI #${row.id}`;
}

function purchaseInvoiceQuickSearchSubtitle(row: {
  mv_status: string | null;
  supplier_name: string | null;
  purchase_order_number: string | null;
}): string | null {
  const pieces = [row.mv_status, row.supplier_name, row.purchase_order_number].filter(
    (piece): piece is string => piece != null && piece.trim() !== "",
  );
  return pieces.length > 0 ? pieces.join(" · ") : null;
}

function gosiQuickSearchLabel(row: { number: string; mv_id: number }): string {
  const trimmed = row.number?.trim() ?? "";
  if (trimmed === "") {
    return `GOSI #${String(row.mv_id)}`;
  }
  return /^gosi/i.test(trimmed) ? trimmed : `GOSI ${trimmed}`;
}

function gosiQuickSearchSubtitle(row: {
  status: string | null;
  sales_order_number: string | null;
  client_name: string | null;
}): string | null {
  const pieces = [row.status, row.sales_order_number, row.client_name].filter(
    (piece): piece is string => piece != null && piece.trim() !== "",
  );
  return pieces.length > 0 ? pieces.join(" · ") : null;
}

function rfqQuickSearchLabel(row: { rfq_number: string; id: number }): string {
  const trimmed = row.rfq_number?.trim() ?? "";
  if (trimmed === "") {
    return `RFQ #${String(row.id)}`;
  }
  return /^rfq/i.test(trimmed) ? trimmed : `RFQ ${trimmed}`;
}

function rfqQuickSearchSubtitle(row: { status: string | null; supplier_name: string | null }): string | null {
  const pieces = [row.status, row.supplier_name].filter(
    (piece): piece is string => piece != null && piece.trim() !== "",
  );
  return pieces.length > 0 ? pieces.join(" · ") : null;
}

function supplierQuickSearchLabel(row: { mv_supplier_name: string; id: number }): string {
  const trimmed = row.mv_supplier_name?.trim() ?? "";
  return trimmed !== "" ? trimmed : `Supplier #${String(row.id)}`;
}

function supplierQuickSearchSubtitle(row: {
  mv_supplier_id: number;
  currency: string | null;
}): string | null {
  const pieces = [
    row.mv_supplier_id > 0 ? `MV ${String(row.mv_supplier_id)}` : null,
    row.currency,
  ].filter((piece): piece is string => piece != null && piece.trim() !== "");
  return pieces.length > 0 ? pieces.join(" · ") : null;
}

function rmaQuickSearchLabel(row: { rmaNumber: string; id: number }): string {
  const trimmed = row.rmaNumber?.trim() ?? "";
  return trimmed !== "" ? trimmed : `RMA #${String(row.id)}`;
}

function rmaQuickSearchSubtitle(row: {
  status: string | null;
  customerName?: string | null;
  endUserName?: string | null;
}): string | null {
  const pieces = [row.status, row.customerName, row.endUserName].filter(
    (piece): piece is string => piece != null && piece.trim() !== "",
  );
  return pieces.length > 0 ? pieces.join(" · ") : null;
}

function shipmentQuickSearchLabel(shipmentId: number): string {
  return `Shipment ${String(shipmentId)}`;
}

function shipmentQuickSearchSubtitle(row: {
  batch_id: number | null;
  manufacturer_sales_order_number: string | null;
  tracking: string | null;
  carrier: string | null;
  mfr_ship_by_date: string | null;
  date_shipped: string | null;
}): string | null {
  const pieces = [
    row.batch_id != null ? `Batch ${String(row.batch_id)}` : null,
    row.manufacturer_sales_order_number,
    row.mfr_ship_by_date != null ? `ship by ${row.mfr_ship_by_date}` : null,
    row.carrier,
    row.tracking,
    row.date_shipped != null ? `shipped ${row.date_shipped}` : null,
  ].filter((piece): piece is string => piece != null && piece.trim() !== "");
  return pieces.length > 0 ? pieces.join(" · ") : null;
}

function QuickSwitcherTriggerHoverHintBody(): JSX.Element {
  return (
    <div className="w-[17rem] max-w-[calc(100vw-2rem)] space-y-2">
      <p>
        Go to a page, field, SKU, wiki page, or document. Press{" "}
        <span className="font-mono text-[11px] text-slate-600">control+/</span> from anywhere.
      </p>
      <div className="space-y-1">
        <p className="font-medium text-slate-800">Document examples</p>
        <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[11px] leading-snug text-slate-600">
          <li>gipi:3569</li>
          <li>@gpo 1909</li>
          <li>gso:201625</li>
          <li>@gosi</li>
          <li>rfq:12</li>
          <li>@vendor</li>
          <li className="col-span-2">rma:2026-08-14</li>
        </ul>
      </div>
      <p className="text-[11px] leading-snug text-slate-600">
        <span className="font-medium text-slate-700">@field</span>,{" "}
        <span className="font-medium text-slate-700">@sku</span>,{" "}
        <span className="font-medium text-slate-700">@wiki</span>, and{" "}
        <span className="font-medium text-slate-700">@page</span> focus those buckets.{" "}
        <span className="font-medium text-slate-700">control+K</span> opens field search directly.
      </p>
    </div>
  );
}

export function QuickSwitcher() {
  const { resetNavigationTrail } = useNavigationTrail();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me, isPending, isError } = useIntegratorMe();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const visibleKeys = useMemo(() => {
    if (isPending || isError) {
      return new Set<string>();
    }
    return visibleKeysForMe(me);
  }, [me, isPending, isError]);
  const canSearchMrpSkus = visibleKeys.has("mrp");
  const canSearchWiki = visibleKeys.has("wiki");
  const canSearchBatches =
    visibleKeys.has("manufacturing_management") || visibleKeys.has("manufacturing");
  const canSearchShipments = visibleKeys.has("shipping");
  const canSearchPurchaseInvoices =
    visibleKeys.has("mrp") ||
    visibleKeys.has("accounting") ||
    visibleKeys.has("manufacturing_management");
  const canSearchGosi = visibleKeys.has("shipping") || visibleKeys.has("inside_sales");
  const canSearchRfq = visibleKeys.has("mrp");
  const canSearchSupplier = visibleKeys.has("mrp");
  const canSearchRma = visibleKeys.has("quality");

  const visiblePages = useMemo(
    () =>
      buildQuickSwitcherCatalogPages()
        .filter((page) => quickNavPageVisibleToKeys(page.path, visibleKeys))
        .filter((page) => {
          if (page.path !== "/inside-sales/sales-orders/new") {
            return true;
          }
          return hubModuleAllowsWrite(me, "inside_sales");
        })
        .map((page) => ({
          kind: "page" as const,
          path: page.path,
          label: page.label,
          group: page.group,
          searchText: page.searchText,
        })),
    [me, visibleKeys],
  );

  const scopedQuery = useMemo(() => parseQuickSwitcherScopedQuery(query), [query]);
  const searchTerm = scopedQuery.term;
  const debouncedQuery = useDebouncedValue(query, 280);
  const debouncedScopedQuery = useMemo(() => parseQuickSwitcherScopedQuery(debouncedQuery), [debouncedQuery]);
  const debouncedSearchTerm = debouncedScopedQuery.term.trim();
  const showPagesForScope = scopedQuery.scope === "all" || scopedQuery.scope === "page";
  const showSkusForScope = scopedQuery.scope === "all" || scopedQuery.scope === "sku";
  const showWikiForScope = scopedQuery.scope === "all" || scopedQuery.scope === "wiki";
  const showPoForScope = scopedQuery.scope === "all" || scopedQuery.scope === "po";
  const showGipiForScope = scopedQuery.scope === "all" || scopedQuery.scope === "gipi";
  const showGosiForScope = scopedQuery.scope === "all" || scopedQuery.scope === "gosi";
  const showRfqForScope = scopedQuery.scope === "all" || scopedQuery.scope === "rfq";
  const showSupplierForScope = scopedQuery.scope === "all" || scopedQuery.scope === "supplier";
  const showRmaForScope = scopedQuery.scope === "all" || scopedQuery.scope === "rma";
  const showQualityRecordsForScope =
    scopedQuery.scope === "all" ||
    scopedQuery.scope === "case" ||
    scopedQuery.scope === "event" ||
    scopedQuery.scope === "ncr" ||
    scopedQuery.scope === "rca" ||
    scopedQuery.scope === "car" ||
    scopedQuery.scope === "loan";
  const showBatchForScope = scopedQuery.scope === "all" || scopedQuery.scope === "batch";
  const showSalesOrderForScope = scopedQuery.scope === "all" || scopedQuery.scope === "so";
  const showShipmentForScope = scopedQuery.scope === "all" || scopedQuery.scope === "shipment";
  const showFieldsForScope = scopedQuery.scope === "field";
  const entitySearchEnabled = debouncedSearchTerm.length >= 2;

  const pageFieldTargets = useMemo(() => {
    if (!open || !showFieldsForScope) return [];
    const main = document.querySelector("main");
    if (!(main instanceof HTMLElement)) return [];
    return collectPageFieldTargets(main);
  }, [open, showFieldsForScope, query]);

  const fieldRows: QuickFieldRow[] = useMemo(() => {
    if (!showFieldsForScope) return [];
    return filterPageFieldTargets(pageFieldTargets, searchTerm)
      .slice(0, 80)
      .map((target) => ({ kind: "field" as const, target }));
  }, [pageFieldTargets, searchTerm, showFieldsForScope]);

  const filteredPages = useMemo(() => {
    if (!showPagesForScope) return [];
    const trimmed = searchTerm.trim();
    const list = filterQuickNavPagesForQuery(visiblePages, trimmed, QUICK_SWITCHER_MODULE_ROOT_PATHS) as QuickNavPage[];
    return [...list].sort((left, right) =>
      compareQuickNavPages(left, right, trimmed, QUICK_SWITCHER_MODULE_ROOT_PATHS),
    );
  }, [visiblePages, searchTerm, showPagesForScope]);

  const entitySearchWants = {
    sku: open && showSkusForScope && canSearchMrpSkus && entitySearchEnabled,
    wiki: open && showWikiForScope && canSearchWiki && entitySearchEnabled,
    salesOrder: open && showSalesOrderForScope && canSearchMrpSkus && entitySearchEnabled,
    purchaseOrder: open && showPoForScope && canSearchMrpSkus && entitySearchEnabled,
    purchaseInvoice: open && showGipiForScope && canSearchPurchaseInvoices && entitySearchEnabled,
    gosi: open && showGosiForScope && canSearchGosi && entitySearchEnabled,
    rfq: open && showRfqForScope && canSearchRfq && entitySearchEnabled,
    supplier: open && showSupplierForScope && canSearchSupplier && entitySearchEnabled,
    rma: open && showRmaForScope && canSearchRma && entitySearchEnabled,
    qualityRecords: open && showQualityRecordsForScope && canSearchRma && entitySearchEnabled,
    shipment: open && showShipmentForScope && canSearchShipments && entitySearchEnabled,
    batch: open && showBatchForScope && canSearchBatches && entitySearchEnabled,
  };

  const skuQuery = useQuery<SkuListResponse, ApiError>({
    queryKey: ["quick-switcher", "skus", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch<SkuListResponse>("/api/mrp/skus", {
        query: {
          q: debouncedSearchTerm,
          page: "1",
          page_size: "12",
        },
        signal,
      }),
    enabled: entitySearchWants.sku,
    staleTime: 20_000,
  });

  const wikiQuery = useQuery<{ results: WikiSearchResult[] }, ApiError>({
    queryKey: ["quick-switcher", "wiki", debouncedSearchTerm],
    queryFn: ({ signal }) => searchWiki(debouncedSearchTerm, 6, { signal }),
    enabled: entitySearchWants.wiki,
    retry: false,
    staleTime: 20_000,
  });

  const salesOrderQuery = useQuery<
    {
      rows: {
        id: number;
        number: string | null;
        status: string | null;
        client_name: string | null;
        sgs_ship_by_date: string | null;
        customer_purchase_order_number: string | null;
      }[];
    },
    ApiError
  >({
    queryKey: ["quick-switcher", "sales-orders", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch("/api/mrp/sales-orders/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.salesOrder,
    staleTime: 20_000,
  });

  const poQuery = useQuery<{ rows: MrpPurchaseOrder[] }, ApiError>({
    queryKey: ["quick-switcher", "purchase-orders", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch<{ rows: MrpPurchaseOrder[] }>("/api/mrp/purchase-orders/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.purchaseOrder,
    staleTime: 20_000,
  });

  const gipiQuery = useQuery<{ rows: QuickPurchaseInvoiceSearchRow[] }, ApiError>({
    queryKey: ["quick-switcher", "purchase-invoices", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch<{ rows: QuickPurchaseInvoiceSearchRow[] }>("/api/mrp/purchase-invoices/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.purchaseInvoice,
    staleTime: 20_000,
  });

  const gosiQuery = useQuery<
    {
      rows: {
        mv_id: number;
        number: string;
        status: string | null;
        sales_order_number: string | null;
        client_name: string | null;
      }[];
    },
    ApiError
  >({
    queryKey: ["quick-switcher", "gosi", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch("/api/shipping/gosi-details/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.gosi,
    staleTime: 20_000,
  });

  const rfqQuery = useQuery<
    {
      rows: {
        id: number;
        rfq_number: string;
        status: string | null;
        supplier_name: string | null;
      }[];
    },
    ApiError
  >({
    queryKey: ["quick-switcher", "rfqs", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch("/api/mrp/rfqs/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.rfq,
    staleTime: 20_000,
  });

  const supplierQuery = useQuery<
    {
      rows: {
        id: number;
        mv_supplier_id: number;
        mv_supplier_name: string;
        currency: string | null;
      }[];
    },
    ApiError
  >({
    queryKey: ["quick-switcher", "suppliers", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch("/api/mrp/suppliers/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.supplier,
    staleTime: 20_000,
  });

  const rmaQuery = useQuery<
    {
      rows: {
        id: number;
        rmaNumber: string;
        status: string | null;
        customerName: string | null;
        endUserName: string | null;
      }[];
    },
    ApiError
  >({
    queryKey: ["quick-switcher", "rmas", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch("/api/quality/rmas/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.rma,
    staleTime: 20_000,
  });

  const qualityRecordKind =
    debouncedScopedQuery.scope === "case" ||
    debouncedScopedQuery.scope === "event" ||
    debouncedScopedQuery.scope === "ncr" ||
    debouncedScopedQuery.scope === "rca" ||
    debouncedScopedQuery.scope === "car" ||
    debouncedScopedQuery.scope === "loan"
      ? debouncedScopedQuery.scope
      : undefined;
  const qualityRecordQuery = useQuery<
    {
      rows: {
        kind: "case" | "event" | "ncr" | "rca" | "car" | "loan";
        id: number;
        label: string;
        subtitle: string | null;
        path: string;
      }[];
    },
    ApiError
  >({
    queryKey: ["quick-switcher", "quality-records", debouncedSearchTerm, qualityRecordKind ?? "all"],
    queryFn: ({ signal }) =>
      apiFetch("/api/quality/records/quick-search", {
        query: {
          q: debouncedSearchTerm,
          kind: qualityRecordKind,
        },
        signal,
      }),
    enabled: entitySearchWants.qualityRecords,
    staleTime: 20_000,
  });

  const shipmentQuery = useQuery<
    {
      rows: {
        shipment_id: number;
        batch_id: number | null;
        manufacturer_sales_order_number: string | null;
        tracking: string | null;
        carrier: string | null;
        mfr_ship_by_date: string | null;
        date_shipped: string | null;
      }[];
    },
    ApiError
  >({
    queryKey: ["quick-switcher", "shipments", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch("/api/shipping/shipments/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.shipment,
    staleTime: 20_000,
  });

  const batchQuery = useQuery<
    {
      rows: {
        batch_id: number;
        mfr_ship_by_date: string | null;
        ship_to_recipient: string | null;
        manufacturer_sales_order_number: string | null;
        gso_numbers: string[];
        location_label: string;
      }[];
    },
    ApiError
  >({
    queryKey: ["quick-switcher", "batches", debouncedSearchTerm],
    queryFn: ({ signal }) =>
      apiFetch("/api/manufacturing/batches/quick-search", {
        query: { q: debouncedSearchTerm, limit: "12" },
        signal,
      }),
    enabled: entitySearchWants.batch,
    staleTime: 20_000,
  });

  const skuRows: QuickSkuRow[] = useMemo(() => {
    const skus = skuQuery.data?.skus ?? [];
    return skus.map((row) => {
      const subtitle = [row.description, row.variant].filter(Boolean).join(" · ") || null;
      return { kind: "sku" as const, sku: row.sku, subtitle };
    });
  }, [skuQuery.data?.skus]);
  const { hasOpenDiscrepancy } = useOpenInventoryDiscrepancySkus(skuRows.map((row) => row.sku));
  const wikiRows: QuickWikiRow[] = useMemo(() => {
    const results = wikiQuery.data?.results ?? [];
    return results.map((result) => ({
      kind: "wiki" as const,
      id: result.id,
      title: result.title,
      subtitle: result.context ?? result.collectionName,
    }));
  }, [wikiQuery.data?.results]);

  const batchRows: QuickBatchRow[] = useMemo(() => {
    const rows = batchQuery.data?.rows ?? [];
    return rows.map((row) => ({
      kind: "batch" as const,
      batchId: row.batch_id,
      label: batchQuickSearchLabel(row.batch_id),
      subtitle: batchQuickSearchSubtitle(row),
    }));
  }, [batchQuery.data?.rows]);

  const poRows: QuickPoRow[] = useMemo(() => {
    const rows = poQuery.data?.rows ?? [];
    return rows.map((row) => {
      const label = row.po_number?.trim() || `PO #${row.id}`;
      const cpnPreview = row.cpns.slice(0, 2).join(", ");
      const subtitleParts = [
        row.line_count > 0 ? `${row.line_count} lines` : null,
        cpnPreview || null,
        row.supplier,
        row.location_code,
      ].filter(Boolean);
      return {
        kind: "po" as const,
        purchaseOrderId: row.id,
        label,
        subtitle: subtitleParts.length > 0 ? subtitleParts.join(" · ") : null,
      };
    });
  }, [poQuery.data?.rows]);

  const gipiRows: QuickGipiRow[] = useMemo(() => {
    const rows = gipiQuery.data?.rows ?? [];
    const listSearchPrefill = debouncedSearchTerm !== "" ? debouncedSearchTerm : null;
    return rows.map((row) => ({
      kind: "gipi" as const,
      purchaseInvoiceId: Number(row.id),
      label: purchaseInvoiceQuickSearchLabel(row),
      subtitle: purchaseInvoiceQuickSearchSubtitle(row),
      listSearchPrefill,
    }));
  }, [gipiQuery.data?.rows, debouncedSearchTerm]);

  const gosiRows: QuickGosiRow[] = useMemo(() => {
    const rows = gosiQuery.data?.rows ?? [];
    return rows.map((row) => ({
      kind: "gosi" as const,
      mvDocumentId: row.mv_id,
      label: gosiQuickSearchLabel(row),
      subtitle: gosiQuickSearchSubtitle(row),
    }));
  }, [gosiQuery.data?.rows]);

  const rfqRows: QuickRfqRow[] = useMemo(() => {
    const rows = rfqQuery.data?.rows ?? [];
    return rows.map((row) => ({
      kind: "rfq" as const,
      rfqId: row.id,
      label: rfqQuickSearchLabel(row),
      subtitle: rfqQuickSearchSubtitle(row),
    }));
  }, [rfqQuery.data?.rows]);

  const supplierRows: QuickSupplierRow[] = useMemo(() => {
    const rows = supplierQuery.data?.rows ?? [];
    return rows.map((row) => ({
      kind: "supplier" as const,
      supplierId: row.id,
      label: supplierQuickSearchLabel(row),
      subtitle: supplierQuickSearchSubtitle(row),
    }));
  }, [supplierQuery.data?.rows]);

  const rmaRows: QuickRmaRow[] = useMemo(() => {
    const rows = rmaQuery.data?.rows ?? [];
    return rows.map((row) => ({
      kind: "rma" as const,
      rmaId: row.id,
      label: rmaQuickSearchLabel(row),
      subtitle: rmaQuickSearchSubtitle(row),
    }));
  }, [rmaQuery.data?.rows]);

  const qualityRecordRows: QuickQualityRecordRow[] = useMemo(() => {
    const rows = qualityRecordQuery.data?.rows ?? [];
    return rows.map((row) => ({
      kind: "quality-record" as const,
      recordKey: `${row.kind}-${row.id}-${row.path}`,
      path: row.path,
      label: row.label,
      subtitle: row.subtitle,
    }));
  }, [qualityRecordQuery.data?.rows]);

  const salesOrderRows: QuickSalesOrderRow[] = useMemo(() => {
    const rows = salesOrderQuery.data?.rows ?? [];
    return rows.map((row) => ({
      kind: "so" as const,
      salesOrderId: row.id,
      label: salesOrderQuickSearchLabel(row.number, row.id),
      subtitle: salesOrderQuickSearchSubtitle(row),
    }));
  }, [salesOrderQuery.data?.rows]);

  const shipmentRows: QuickShipmentRow[] = useMemo(() => {
    const rows = shipmentQuery.data?.rows ?? [];
    return rows.map((row) => ({
      kind: "shipment" as const,
      shipmentId: row.shipment_id,
      label: shipmentQuickSearchLabel(row.shipment_id),
      subtitle: shipmentQuickSearchSubtitle(row),
    }));
  }, [shipmentQuery.data?.rows]);

  const quickSwitcherSectionOffsets = useMemo(
    () => {
      const pages = filteredPages.length;
      const skus = pages;
      const wiki = pages + skuRows.length;
      const salesOrders = wiki + wikiRows.length;
      const purchaseOrders = salesOrders + salesOrderRows.length;
      const purchaseInvoices = purchaseOrders + poRows.length;
      const gosi = purchaseInvoices + gipiRows.length;
      const rfqs = gosi + gosiRows.length;
      const suppliers = rfqs + rfqRows.length;
      const rmas = suppliers + supplierRows.length;
      const qualityRecords = rmas + rmaRows.length;
      const batches = qualityRecords + qualityRecordRows.length;
      const shipments = batches + batchRows.length;
      return {
        pages,
        skus,
        wiki,
        salesOrders,
        purchaseOrders,
        purchaseInvoices,
        gosi,
        rfqs,
        suppliers,
        rmas,
        qualityRecords,
        batches,
        shipments,
      };
    },
    [
      filteredPages.length,
      skuRows.length,
      wikiRows.length,
      salesOrderRows.length,
      poRows.length,
      gipiRows.length,
      gosiRows.length,
      rfqRows.length,
      supplierRows.length,
      rmaRows.length,
      qualityRecordRows.length,
      batchRows.length,
    ],
  );

  const flatRows: QuickSwitcherRow[] = useMemo(() => {
    if (showFieldsForScope) return fieldRows;
    return [
      ...filteredPages.map((page) => page as QuickSwitcherRow),
      ...skuRows,
      ...wikiRows,
      ...salesOrderRows,
      ...poRows,
      ...gipiRows,
      ...gosiRows,
      ...rfqRows,
      ...supplierRows,
      ...rmaRows,
      ...qualityRecordRows,
      ...batchRows,
      ...shipmentRows,
    ];
  }, [
    showFieldsForScope,
    fieldRows,
    filteredPages,
    skuRows,
    wikiRows,
    salesOrderRows,
    poRows,
    gipiRows,
    gosiRows,
    rfqRows,
    supplierRows,
    rmaRows,
    qualityRecordRows,
    batchRows,
    shipmentRows,
  ]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, open]);

  useEffect(() => {
    setHighlightedIndex((previous) =>
      flatRows.length === 0 ? 0 : Math.min(previous, flatRows.length - 1),
    );
  }, [flatRows.length]);

  useEffect(() => {
    if (!open || flatRows.length === 0) return;
    document.getElementById(`quick-switch-${highlightedIndex}`)?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, flatRows.length, open]);

  const closeAfterSameTabNavigate = useCallback(() => {
    resetNavigationTrail();
    void queryClient.cancelQueries({ queryKey: ["quick-switcher"] });
    setOpen(false);
    setQuery("");
  }, [queryClient, resetNavigationTrail]);

  const goToRow = (row: QuickSwitcherRow) => {
    if (row.kind === "field") {
      void queryClient.cancelQueries({ queryKey: ["quick-switcher"] });
      setOpen(false);
      setQuery("");
      jumpToPageField(row.target);
      return;
    }
    closeAfterSameTabNavigate();
    navigate(quickSwitcherRowPath(row));
  };

  const repositionQuickPanel = useCallback(() => {
    if (!open || !triggerRef.current) {
      setMenuBox(null);
      return;
    }
    const desiredWidth = Math.min(window.innerWidth - 24, 22 * 16);
    setMenuBox(
      computeHorizontalMenuPanel({
        anchorRect: triggerRef.current.getBoundingClientRect(),
        menuWidth: desiredWidth,
        horizontalAlign: "end",
        gap: 8,
        maxHeightCap: Math.min(560, Math.floor(window.innerHeight * 0.9)),
      }),
    );
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return;
    }
    triggerRef.current?.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
    repositionQuickPanel();
    const onLayout = (): void => {
      repositionQuickPanel();
    };
    return registerViewportLayoutListeners(onLayout);
  }, [open, repositionQuickPanel]);

  useEffect(() => {
    const onKeyDown = (event: Event) => {
      if (!(event instanceof globalThis.KeyboardEvent)) return;
      if (!isGoToShortcut(event)) return;
      event.preventDefault();
      setOpen(true);
      window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      if (panelRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: Event) => {
      if (!(event instanceof globalThis.KeyboardEvent)) return;
      if (event.key === "Escape") {
        void queryClient.cancelQueries({ queryKey: ["quick-switcher"] });
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, queryClient]);

  useEffect(() => {
    if (open) {
      return;
    }
    void queryClient.cancelQueries({ queryKey: ["quick-switcher"] });
  }, [open, queryClient]);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [open]);

  const cartonAdjustPath = inventoryAdjustScanTarget("carton", query);
  const cartonAdjustLabel =
    cartonAdjustPath == null
      ? null
      : `Carton ${inventoryBoxShortCodeFromHex(cartonAdjustPath.slice("/i/".length))}`;

  const onListKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && cartonAdjustPath != null) {
      event.preventDefault();
      closeAfterSameTabNavigate();
      navigate(cartonAdjustPath);
      return;
    }
    if (flatRows.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, flatRows.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const row = flatRows[highlightedIndex];
      if (row) goToRow(row);
    }
  };

  const showSkuSection =
    showSkusForScope &&
    canSearchMrpSkus &&
    entitySearchEnabled &&
    (skuQuery.isFetching || skuRows.length > 0 || skuQuery.isError);
  const showBatchSection =
    showBatchForScope &&
    canSearchBatches &&
    entitySearchEnabled &&
    (batchQuery.isFetching || batchRows.length > 0 || batchQuery.isError);
  const showSalesOrderSection =
    showSalesOrderForScope &&
    canSearchMrpSkus &&
    entitySearchEnabled &&
    (salesOrderQuery.isFetching || salesOrderRows.length > 0 || salesOrderQuery.isError);
  const showShipmentSection =
    showShipmentForScope &&
    canSearchShipments &&
    entitySearchEnabled &&
    (shipmentQuery.isFetching || shipmentRows.length > 0 || shipmentQuery.isError);
  const showPoSection =
    showPoForScope &&
    canSearchMrpSkus &&
    entitySearchEnabled &&
    (poQuery.isFetching || poRows.length > 0 || poQuery.isError);
  const showGipiSection =
    showGipiForScope &&
    canSearchPurchaseInvoices &&
    entitySearchEnabled &&
    (gipiQuery.isFetching || gipiRows.length > 0 || gipiQuery.isError);
  const showGosiSection =
    showGosiForScope &&
    canSearchGosi &&
    entitySearchEnabled &&
    (gosiQuery.isFetching || gosiRows.length > 0 || gosiQuery.isError || gosiQuery.isFetched);
  const showRfqSection =
    showRfqForScope &&
    canSearchRfq &&
    entitySearchEnabled &&
    (rfqQuery.isFetching || rfqRows.length > 0 || rfqQuery.isError);
  const showSupplierSection =
    showSupplierForScope &&
    canSearchSupplier &&
    entitySearchEnabled &&
    (supplierQuery.isFetching || supplierRows.length > 0 || supplierQuery.isError);
  const showRmaSection =
    showRmaForScope &&
    canSearchRma &&
    entitySearchEnabled &&
    (rmaQuery.isFetching || rmaRows.length > 0 || rmaQuery.isError);
  const showQualityRecordSection =
    showQualityRecordsForScope &&
    canSearchRma &&
    entitySearchEnabled &&
    (qualityRecordQuery.isFetching || qualityRecordRows.length > 0 || qualityRecordQuery.isError);
  const showWikiSection =
    showWikiForScope &&
    canSearchWiki &&
    entitySearchEnabled &&
    (wikiQuery.isFetching || wikiRows.length > 0 || wikiQuery.isError);
  const scopedEmptySearch = scopedQuery.scope !== "all" && scopedQuery.term.trim().length === 0;
  const scopedFocusLabel = (
    {
      sku: "@sku",
      wiki: "@wiki",
      po: "@gpo",
      gipi: "@gipi",
      gosi: "@gosi",
      rfq: "@rfq",
      supplier: "@vendor",
      rma: "@rma",
      case: "@case",
      event: "@event",
      ncr: "@ncr",
      rca: "@rca",
      car: "@car",
      loan: "@loan",
      batch: "@batch",
      so: "@gso",
      shipment: "@shipment",
      field: "@field",
      page: "@page",
      all: "@page",
      "end-user": "@end-user",
      client: "@client",
    } as const
  )[scopedQuery.scope];
  const hasEntitySections =
    showBatchSection ||
    showSalesOrderSection ||
    showShipmentSection ||
    showPoSection ||
    showGipiSection ||
    showGosiSection ||
    showRfqSection ||
    showSupplierSection ||
    showRmaSection ||
    showQualityRecordSection ||
    showSkuSection ||
    showWikiSection;
  const {
    skus: skusOffset,
    wiki: wikiOffset,
    salesOrders: salesOrdersOffset,
    purchaseOrders: purchaseOrdersOffset,
    purchaseInvoices: purchaseInvoicesOffset,
    gosi: gosiOffset,
    rfqs: rfqsOffset,
    suppliers: suppliersOffset,
    rmas: rmasOffset,
    qualityRecords: qualityRecordsOffset,
    batches: batchesOffset,
    shipments: shipmentsOffset,
  } = quickSwitcherSectionOffsets;

  return (
    <div ref={containerRef} className="relative flex flex-shrink-0 items-center">
      <HoverHintTrigger
        align="end"
        trigger={
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label="Go to a page, field, or document (control+/)"
            onClick={() => {
              setOpen((current) => !current);
            }}
            className={cn(
              "inline-flex max-w-[min(100%,14rem)] items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-left text-sm text-slate-600",
              "hover:border-slate-300 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
            )}
          >
            <Search className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
            <span className="min-w-0 truncate">Go to…</span>
            <kbd
              className="hidden shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-500 sm:inline"
              aria-hidden
            >
              ^/
            </kbd>
          </button>
        }
      >
        <QuickSwitcherTriggerHoverHintBody />
      </HoverHintTrigger>

      {open && menuBox
        ? createPortal(
            <div
              ref={panelRef}
              className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
              role="presentation"
              style={{
                position: "fixed",
                top: menuBox.top,
                left: menuBox.left,
                width: menuBox.width,
                maxHeight: menuBox.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
          <label className="block shrink-0 border-b border-slate-100 px-3 py-2">
            <span className="sr-only">
              Search pages, fields, documents, SKUs, and wiki pages
            </span>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onListKeyDown}
                placeholder="Page, GIPI 3569, gpo:1909, gosi:168896…"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                className={cn(
                  "w-full rounded-md border border-slate-200 py-2 pl-9 pr-20 text-sm",
                  "text-slate-900 placeholder:text-slate-400",
                  "focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
                )}
              />
              <span className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 text-[10px] text-slate-400">
                <CornerDownLeft className="h-3 w-3" aria-hidden /> enter
              </span>
            </div>
          </label>

          <div
            className="min-h-0 flex-1 overflow-y-auto py-2"
            role="listbox"
            aria-activedescendant={
              flatRows[highlightedIndex]
                ? `quick-switch-${highlightedIndex}`
                : undefined
            }
          >
            {cartonAdjustPath != null && cartonAdjustLabel != null ? (
              <div className="px-1">
                <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Carton
                </div>
                <ul className="space-y-0.5">
                  <QuickSwitcherOption
                    flatIndex={-1}
                    active={false}
                    to={cartonAdjustPath}
                    onMouseEnter={() => setHighlightedIndex(0)}
                    onSameTabNavigate={closeAfterSameTabNavigate}
                  >
                    <span className="font-medium">{cartonAdjustLabel}</span>
                    <span className="truncate text-xs text-slate-500">{cartonAdjustPath}</span>
                  </QuickSwitcherOption>
                </ul>
              </div>
            ) : null}
            {showFieldsForScope ? (
              fieldRows.length > 0 ? (
                <div className="px-1">
                  <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Fields on this page
                  </div>
                  <ul className="space-y-0.5">
                    {fieldRows.map((row, fieldIndex) => {
                      const flatIndex = fieldIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <li key={row.target.id} role="presentation">
                          <button
                            type="button"
                            role="option"
                            id={`quick-switch-${flatIndex}`}
                            aria-selected={active}
                            onMouseEnter={() => setHighlightedIndex(flatIndex)}
                            onClick={() => goToRow(row)}
                            className={cn(
                              "flex w-full flex-col rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                              active ? "bg-brand-50 text-brand-900" : "text-slate-700 hover:bg-slate-50",
                            )}
                          >
                            <span className="font-medium">{row.target.label}</span>
                            {row.target.section ? (
                              <span className="truncate text-xs text-slate-500">{row.target.section}</span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : scopedEmptySearch ? (
                <p className="px-4 py-3 text-center text-sm text-slate-500">
                  Type a search after {scopedFocusLabel}.
                </p>
              ) : query.trim().length > 0 ? (
                <p className="px-4 py-3 text-center text-sm text-slate-500">No fields match on this page.</p>
              ) : (
                <p className="px-4 py-3 text-center text-sm text-slate-500">
                  No searchable fields were found on this page.
                </p>
              )
            ) : filteredPages.length > 0 ? (
              <div className="px-1">
                <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Pages
                </div>
                <ul className="space-y-0.5">
                  {filteredPages.map((page, indexInPages) => {
                    const flatIndex = indexInPages;
                    const active = highlightedIndex === flatIndex;
                    return (
                      <QuickSwitcherOption
                        key={page.path}
                        flatIndex={flatIndex}
                        active={active}
                        to={quickSwitcherRowPath(page)}
                        onMouseEnter={() => setHighlightedIndex(flatIndex)}
                        onSameTabNavigate={closeAfterSameTabNavigate}
                      >
                        <span className="font-medium">{page.label}</span>
                        <span className="truncate text-xs text-slate-500">{page.path}</span>
                      </QuickSwitcherOption>
                    );
                  })}
                </ul>
              </div>
            ) : query.trim().length > 0 && !hasEntitySections && !scopedEmptySearch ? (
              <p className="px-4 py-3 text-center text-sm text-slate-500">
                {scopedQuery.scope === "po"
                  ? "No purchase order matches."
                  : scopedQuery.scope === "gipi"
                    ? "No purchase invoice matches."
                    : scopedQuery.scope === "gosi"
                      ? "No sales invoice matches."
                      : scopedQuery.scope === "rfq"
                        ? "No RFQ matches."
                        : scopedQuery.scope === "supplier"
                          ? "No supplier matches."
                        : scopedQuery.scope === "rma"
                          ? "No RMA matches."
                        : scopedQuery.scope === "case"
                          ? "No quality case matches."
                        : scopedQuery.scope === "event"
                          ? "No quality event matches."
                        : scopedQuery.scope === "ncr"
                          ? "No NCR matches."
                        : scopedQuery.scope === "rca"
                          ? "No RCA matches."
                        : scopedQuery.scope === "car"
                          ? "No corrective action matches."
                        : scopedQuery.scope === "loan"
                          ? "No analysis loan matches."
                    : scopedQuery.scope === "batch"
                      ? "No batch matches."
                      : scopedQuery.scope === "so"
                        ? "No sales order matches."
                        : scopedQuery.scope === "shipment"
                          ? "No shipment matches."
                          : scopedQuery.scope === "sku"
                            ? "No SKU matches."
                            : scopedQuery.scope === "wiki"
                              ? "No wiki page matches."
                              : "No matching pages."}
              </p>
            ) : scopedEmptySearch ? (
              <p className="px-4 py-3 text-center text-sm text-slate-500">
                Type a search after {scopedFocusLabel}.
              </p>
            ) : query.trim().length === 0 ? (
              <p className="px-4 py-3 text-center text-sm text-slate-500">
                Type to search, or use gipi:3569, @gpo 1909, gso:201625, @gosi, rfq:12, @vendor, rma:2026-08-14, @case, @event, @ncr, @rca, @car, @loan, @field, @sku, or @wiki.
                Control+K opens field search directly. A SKU also finds matching purchase order lines unless you use @sku.
              </p>
            ) : null}

            {showSkuSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  SKUs
                  {skuQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {skuQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">SKU search is not available.</p>
                ) : skuRows.length === 0 && !skuQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No SKU matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {skuRows.map((row, skuIndex) => {
                      const flatIndex = skusOffset + skuIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.sku}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="flex items-center gap-2">
                            <span className="font-mono font-medium">{row.sku}</span>
                            {hasOpenDiscrepancy(row.sku) ? <OpenInventoryDiscrepancySkuBadge sku={row.sku} /> : null}
                          </span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showWikiSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Wiki
                  {wikiQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {wikiQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-slate-500">
                    {wikiQuery.error.status === 409 ? "Connect Outline to search wiki pages." : "Wiki search is not available."}
                  </p>
                ) : wikiRows.length === 0 && !wikiQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No wiki pages match.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {wikiRows.map((row, wikiIndex) => {
                      const flatIndex = wikiOffset + wikiIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.id}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-medium">{row.title}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showSalesOrderSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Sales orders
                  {salesOrderQuery.isFetching ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" />
                  ) : null}
                </div>
                {salesOrderQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">Sales order search is not available.</p>
                ) : salesOrderRows.length === 0 && !salesOrderQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No sales order matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {salesOrderRows.map((row, salesOrderIndex) => {
                      const flatIndex = salesOrdersOffset + salesOrderIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.salesOrderId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-mono font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showPoSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Purchase orders
                  {poQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {poQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">Purchase order search is not available.</p>
                ) : poRows.length === 0 && !poQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No purchase order matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {poRows.map((row, poIndex) => {
                      const flatIndex = purchaseOrdersOffset + poIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.purchaseOrderId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-mono font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showGipiSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Purchase invoices
                  {gipiQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {gipiQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">Purchase invoice search is not available.</p>
                ) : gipiRows.length === 0 && !gipiQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No purchase invoice matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {gipiRows.map((row, gipiIndex) => {
                      const flatIndex = purchaseInvoicesOffset + gipiIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.purchaseInvoiceId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-mono font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showGosiSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Sales invoices
                  {gosiQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {gosiQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">Sales invoice search is not available.</p>
                ) : gosiRows.length === 0 && !gosiQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No sales invoice matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {gosiRows.map((row, gosiIndex) => {
                      const flatIndex = gosiOffset + gosiIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.mvDocumentId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-mono font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showRfqSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  RFQs
                  {rfqQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {rfqQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">RFQ search is not available.</p>
                ) : rfqRows.length === 0 && !rfqQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No RFQ matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {rfqRows.map((row, rfqIndex) => {
                      const flatIndex = rfqsOffset + rfqIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.rfqId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-mono font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showSupplierSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Suppliers
                  {supplierQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {supplierQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">Supplier search is not available.</p>
                ) : supplierRows.length === 0 && !supplierQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No supplier matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {supplierRows.map((row, supplierIndex) => {
                      const flatIndex = suppliersOffset + supplierIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.supplierId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showRmaSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  RMAs
                  {rmaQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {rmaQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">RMA search is not available.</p>
                ) : rmaRows.length === 0 && !rmaQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No RMA matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {rmaRows.map((row, rmaIndex) => {
                      const flatIndex = rmasOffset + rmaIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.rmaId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-mono font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showQualityRecordSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Quality records
                  {qualityRecordQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {qualityRecordQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">Quality record search is not available.</p>
                ) : qualityRecordRows.length === 0 && !qualityRecordQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No quality record matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {qualityRecordRows.map((row, recordIndex) => {
                      const flatIndex = qualityRecordsOffset + recordIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.recordKey}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showBatchSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Batches
                  {batchQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {batchQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">Batch search is not available.</p>
                ) : batchRows.length === 0 && !batchQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No batch matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {batchRows.map((row, batchIndex) => {
                      const flatIndex = batchesOffset + batchIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.batchId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {showShipmentSection ? (
              <div className="mt-2 border-t border-slate-100 px-1 pt-2">
                <div className="flex items-center gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Shipments
                  {shipmentQuery.isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Loading" /> : null}
                </div>
                {shipmentQuery.isError ? (
                  <p className="px-4 py-2 text-sm text-red-700">Shipment search is not available.</p>
                ) : shipmentRows.length === 0 && !shipmentQuery.isFetching ? (
                  <p className="px-4 py-2 text-sm text-slate-500">No shipment matches.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {shipmentRows.map((row, shipmentIndex) => {
                      const flatIndex = shipmentsOffset + shipmentIndex;
                      const active = highlightedIndex === flatIndex;
                      return (
                        <QuickSwitcherOption
                          key={row.shipmentId}
                          flatIndex={flatIndex}
                          active={active}
                          to={quickSwitcherRowPath(row)}
                          onMouseEnter={() => setHighlightedIndex(flatIndex)}
                          onSameTabNavigate={closeAfterSameTabNavigate}
                        >
                          <span className="font-medium">{row.label}</span>
                          {row.subtitle ? (
                            <span className="truncate text-xs text-slate-500">{row.subtitle}</span>
                          ) : null}
                        </QuickSwitcherOption>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}
          </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
